import { useMemo, useState } from "react";
import { PieChart, Plus, X } from "lucide-react";
import { Dialog } from "@/components/modals";
import TaxonomyPicker from "@/components/courses/TaxonomyPicker";
import { taxonomyKindLabel, type TaxonomyOption } from "@/components/courses/courseMeta";
import { HOUR_BUCKETS } from "@/lib/activityAnatomy";
import {
  MINUTE_CATEGORY_EXTRAS,
  compactSplit,
  extraMinutes,
  splitInvalid,
  splitSum,
  type MinuteTypeSplitPart,
  type MinuteTypeSplits,
} from "@/lib/minuteCategoryAwards";
import "@/components/courses/CourseMetaFields.css";

type Slot = "primary" | "secondary" | "tertiary";
type PickerSlot = "add" | "secondary" | "tertiary";

function isConcentration(options: TaxonomyOption[], id: string) {
  if (!id) return false;
  return options.find((row) => String(row.id) === id)?.type === "concentration";
}

function omitSlots(splits: MinuteTypeSplits, slots: Slot[]): MinuteTypeSplits {
  const next: MinuteTypeSplits = { ...splits };
  for (const slot of slots) delete next[slot];
  return next;
}

function slotMinutes(slot: Slot, estimatedMinutes: number, secondaryId: string, tertiaryId: string) {
  const primary = Math.max(0, Number(estimatedMinutes) || 0);
  if (slot === "primary") return primary;
  if (slot === "secondary") {
    return secondaryId ? extraMinutes(primary, MINUTE_CATEGORY_EXTRAS.secondaryExtraPercent) : 0;
  }
  return tertiaryId ? extraMinutes(primary, MINUTE_CATEGORY_EXTRAS.tertiaryExtraPercent) : 0;
}

export function activityMinuteTypesInvalid({
  options,
  coursePrimaryId,
  secondaryId,
  tertiaryId,
  estimatedMinutes,
  minuteTypeSplits,
}: {
  options: TaxonomyOption[];
  coursePrimaryId: string;
  secondaryId: string;
  tertiaryId: string;
  estimatedMinutes: number;
  minuteTypeSplits: MinuteTypeSplits;
}): string | null {
  const slots: Array<{ slot: Slot; id: string; required: boolean }> = [
    { slot: "primary", id: coursePrimaryId, required: isConcentration(options, coursePrimaryId) },
    { slot: "secondary", id: secondaryId, required: isConcentration(options, secondaryId) },
    { slot: "tertiary", id: tertiaryId, required: isConcentration(options, tertiaryId) },
  ];
  for (const row of slots) {
    if (!row.id) continue;
    const total = slotMinutes(row.slot, estimatedMinutes, secondaryId, tertiaryId);
    if (!splitInvalid(minuteTypeSplits[row.slot], total, row.required)) continue;
    if (row.required && splitSum(minuteTypeSplits[row.slot]) <= 0) {
      return "Concentration categories need at least one minute type greater than 0.";
    }
    return "Minute types must add up to that category’s minutes.";
  }
  return null;
}

const MINUTE_TYPE_INPUT = /^\d*\.?\d{0,2}$/;

function parseMinuteTypeInput(raw: string): number {
  const text = raw.trim();
  if (!text || text === ".") return 0;
  const n = Number(text);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100) / 100;
}

function MinuteTypeSplitForm({
  total,
  value,
  required,
  disabled,
  onChange,
}: {
  total: number;
  value: MinuteTypeSplitPart | undefined;
  required: boolean;
  disabled: boolean;
  onChange: (next: MinuteTypeSplitPart) => void;
}) {
  const shown = compactSplit(value);
  const [draft, setDraft] = useState<Partial<Record<string, string>>>({});
  const sum = splitSum(shown);
  const remaining = Math.round((total - sum) * 100) / 100;
  const noneSet = sum <= 0;
  const mismatch = splitInvalid(shown, total, required);

  return (
    <div className="al-cb-hour-splits">
      <p className={`al-cb-count${mismatch ? " al-cb-hour-splits-off" : ""}`}>
        {required && noneSet
          ? `Set at least one type · must equal ${total} min`
          : remaining === 0 && !noneSet
            ? `Split ${total} minutes`
            : noneSet
              ? `Optional · split ${total} minutes by type`
              : `${Math.abs(remaining)} min ${remaining > 0 ? "left" : "over"} · must equal ${total}`}
      </p>
      {HOUR_BUCKETS.map((bucket) => (
        <label key={bucket.value} className="al-cb-hour-split">
          <span>{bucket.label}</span>
          <input
            type="text"
            inputMode="decimal"
            className="al-cb-input"
            value={
              draft[bucket.value] !== undefined
                ? draft[bucket.value]
                : shown[bucket.value]
                  ? String(shown[bucket.value])
                  : ""
            }
            disabled={disabled}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw !== "" && !MINUTE_TYPE_INPUT.test(raw)) return;
              setDraft((prev) => ({ ...prev, [bucket.value]: raw }));
              onChange(compactSplit({ ...shown, [bucket.value]: parseMinuteTypeInput(raw) }));
            }}
            onBlur={() => {
              setDraft((prev) => {
                const next = { ...prev };
                delete next[bucket.value];
                return next;
              });
            }}
          />
        </label>
      ))}
    </div>
  );
}

export default function ActivityCategoryFields({
  coursePrimaryId,
  secondaryId,
  tertiaryId,
  estimatedMinutes,
  minuteTypeSplits,
  options,
  loading,
  disabled = false,
  onSecondary,
  onTertiary,
  onMinuteTypeSplits,
  onOptionsChange,
}: {
  coursePrimaryId: string;
  secondaryId: string;
  tertiaryId: string;
  estimatedMinutes: number;
  minuteTypeSplits: MinuteTypeSplits;
  options: TaxonomyOption[];
  loading: boolean;
  disabled?: boolean;
  onSecondary: (id: string) => void;
  onTertiary: (id: string) => void;
  onMinuteTypeSplits: (next: MinuteTypeSplits) => void;
  onOptionsChange?: (options: TaxonomyOption[]) => void;
}) {
  const [picker, setPicker] = useState<PickerSlot | null>(null);
  const [splitSlot, setSplitSlot] = useState<Slot | null>(null);

  const primary = options.find((row) => String(row.id) === coursePrimaryId) || null;
  const secondary = options.find((row) => String(row.id) === secondaryId) || null;
  const tertiary = options.find((row) => String(row.id) === tertiaryId) || null;

  const extraOptions = useMemo(
    () =>
      options.filter((row) => {
        const id = String(row.id);
        if (id === coursePrimaryId) return false;
        if (picker === "secondary") return id !== tertiaryId;
        if (picker === "tertiary") return id !== secondaryId;
        return id !== secondaryId && id !== tertiaryId;
      }),
    [options, coursePrimaryId, secondaryId, tertiaryId, picker],
  );

  const primaryMinutes = slotMinutes("primary", estimatedMinutes, secondaryId, tertiaryId);
  const secondaryMinutes = slotMinutes("secondary", estimatedMinutes, secondaryId, tertiaryId);
  const tertiaryMinutes = slotMinutes("tertiary", estimatedMinutes, secondaryId, tertiaryId);

  const canAdd = !disabled && Boolean(coursePrimaryId) && (!secondaryId || !tertiaryId);

  const assignExtra = (id: string) => {
    if (!id) return;
    const picked = options.find((row) => String(row.id) === id);
    const cte = !picked || picked.type === "concentration";
    const target: Slot =
      picker === "tertiary" || (picker === "add" && Boolean(secondaryId)) ? "tertiary" : "secondary";
    if (target === "secondary") onSecondary(id);
    else onTertiary(id);
    setPicker(null);
    if (cte) setSplitSlot(target);
  };

  const openAdd = () => {
    if (disabled || !canAdd) return;
    setPicker((open) => (open === "add" ? null : "add"));
  };

  const chips: Array<{
    slot: Slot;
    id: string;
    row: TaxonomyOption | null;
    minutes: number;
    caption: string;
    required: boolean;
    clearable: boolean;
  }> = [
    {
      slot: "primary",
      id: coursePrimaryId,
      row: primary,
      minutes: primaryMinutes,
      caption: `100% of estimated minutes${primaryMinutes ? ` · ${primaryMinutes} min` : ""}`,
      required: isConcentration(options, coursePrimaryId),
      clearable: false,
    },
  ];
  if (secondaryId) {
    chips.push({
      slot: "secondary",
      id: secondaryId,
      row: secondary,
      minutes: secondaryMinutes,
      caption: `${MINUTE_CATEGORY_EXTRAS.secondaryExtraPercent}% extra of primary${secondaryMinutes ? ` · ${secondaryMinutes} min` : ""}`,
      required: isConcentration(options, secondaryId),
      clearable: !disabled,
    });
  }
  if (tertiaryId) {
    chips.push({
      slot: "tertiary",
      id: tertiaryId,
      row: tertiary,
      minutes: tertiaryMinutes,
      caption: `${MINUTE_CATEGORY_EXTRAS.tertiaryExtraPercent}% extra of primary${tertiaryMinutes ? ` · ${tertiaryMinutes} min` : ""}`,
      required: isConcentration(options, tertiaryId),
      clearable: !disabled,
    });
  }

  const splitChip = chips.find((chip) => chip.slot === splitSlot) || null;
  const pickerSelected =
    picker === "secondary" ? secondaryId : picker === "tertiary" ? tertiaryId : "";

  return (
    <div className="al-cb-cat-block">
      <label className="al-cb-label">Categories</label>
      <div className="al-cb-cat-bar">
      {chips.map((chip) => {
        const bad = splitInvalid(minuteTypeSplits[chip.slot], chip.minutes, chip.required);
        const title = chip.row?.title || (chip.slot === "primary" && loading ? "Loading…" : chip.slot === "primary" ? "Set on the course" : "Category");
        const kind = chip.row ? taxonomyKindLabel(chip.row.type) : null;
        return (
          <div key={chip.slot} className="al-cb-cat-chip">
            <div className="al-cb-cat-chip-row">
              {chip.clearable ? (
                <button
                  type="button"
                  className="al-cb-badge"
                  disabled={disabled}
                  title="Change category"
                  onClick={() => setPicker((open) => (open === chip.slot ? null : (chip.slot as PickerSlot)))}
                >
                  {title}
                  {kind ? ` · ${kind}` : ""}
                </button>
              ) : (
                <span className="al-cb-badge">{title}{kind ? ` · ${kind}` : ""}</span>
              )}
              {chip.minutes > 0 ? (
                <button
                  type="button"
                  className={`al-cb-cat-split${bad ? " al-cb-cat-split--bad" : ""}`}
                  disabled={disabled}
                  title={
                    chip.required
                      ? bad
                        ? "Minute types required for this Concentration"
                        : "Split minutes by type"
                      : "Split minutes by type"
                  }
                  aria-label={
                    chip.required && bad
                      ? "Minute types required for this Concentration"
                      : "Split minutes by type"
                  }
                  onClick={() => setSplitSlot(chip.slot)}
                >
                  <PieChart size={13} strokeWidth={2.25} aria-hidden="true" />
                </button>
              ) : null}
              {chip.clearable ? (
                <button
                  type="button"
                  className="al-cb-cat-chip-x"
                  disabled={disabled}
                  title="Remove category"
                  aria-label="Remove category"
                  onClick={() => {
                    if (chip.slot === "secondary") {
                      onSecondary("");
                      onTertiary("");
                      onMinuteTypeSplits(omitSlots(minuteTypeSplits, ["secondary", "tertiary"]));
                    } else {
                      onTertiary("");
                      onMinuteTypeSplits(omitSlots(minuteTypeSplits, ["tertiary"]));
                    }
                    setPicker(null);
                  }}
                >
                  <X size={11} aria-hidden="true" />
                </button>
              ) : null}
            </div>
            <p className="al-cb-count">{chip.caption}</p>
          </div>
        );
      })}

      {canAdd ? (
        <div className="al-cb-cat-add-wrap">
          <button
            type="button"
            className={`al-cb-cat-add${picker === "add" ? " al-cb-cat-add--on" : ""}`}
            title="Add category"
            aria-label="Add category"
            aria-expanded={picker === "add"}
            onClick={openAdd}
          >
            <Plus size={14} aria-hidden="true" />
          </button>
        </div>
      ) : null}

      {picker ? (
        <div className="al-cb-cat-pop">
          <TaxonomyPicker
            selectedId={pickerSelected}
            options={extraOptions}
            loading={loading}
            disabled={disabled}
            startClosed
            listOpen
            onListOpenChange={(open) => {
              if (!open) setPicker(null);
            }}
            emptyLabel="Pick a category"
            onSelect={(id) => {
              if (!id) return;
              assignExtra(id);
            }}
            onOptionsChange={onOptionsChange}
          />
        </div>
      ) : null}
      </div>

      <Dialog
        open={Boolean(splitChip)}
        title="Minute types"
        subtitle={
          splitChip
            ? `${splitChip.row?.title || "Category"} · ${splitChip.minutes} min`
            : undefined
        }
        width="22rem"
        onClose={() => setSplitSlot(null)}
        footer={
          <button type="button" className="al-cb-btn" onClick={() => setSplitSlot(null)}>
            Done
          </button>
        }
      >
        {splitChip ? (
          <MinuteTypeSplitForm
            total={splitChip.minutes}
            value={minuteTypeSplits[splitChip.slot]}
            required={splitChip.required}
            disabled={disabled}
            onChange={(part) => onMinuteTypeSplits({ ...minuteTypeSplits, [splitChip.slot]: part })}
          />
        ) : null}
      </Dialog>
    </div>
  );
}
